<template>
    <div>
        <Loader v-if="loading" />
        <div v-else>
            <div class="container-fluid mt-45">
                <div class="row mb-4">
                    <div class="col-md-6">
                        <Breadcrumbs :testNumber="test.testNumber" :status="test.status" />
                    </div>

                    <div class="col-md-6 text-right">
                        <button :disabled="isBusy" @click="downloadResult(false)" class="btn btn-success btn-block-mobile btn-lg lg-shadow mr-2" :class="{disabled : isBusy}">Download CSV</button>
                        <button :disabled="isBusy" @click="downloadResult(true)" class="btn btn-success btn-block-mobile btn-lg lg-shadow mr-2" :class="{disabled : isBusy}">Download PDF</button>
                        <button :disabled="!canShowCancel" @click="cancelTest()" class="btn btn-danger btn-cancel-test btn-lg btn-block-mobile lg-shadow mt-4 mt-md-0" :class="{disabled : !canShowCancel}">Cancel test</button>
                    </div>
                </div>

                <div class="d-flex flex-wrap flex-row-gap-20">
                    <div v-if="test.replacesTestId != null" class="result-banner replaces">
                        This test is a replacement for - <span @click="viewTest(test.replacesTestId)" class="underlined text-capitalize cursor-pointer">Test #{{ test.replacesTestNumber }}</span>
                    </div>

                    <div v-if="test.replacedByTestId != null" class="result-banner reordered">
                        This test has been reordered - <span @click="viewTest(test.replacedByTestId)" class="underlined text-capitalize cursor-pointer">Test #{{ test.replacedByTestNumber }}</span>
                    </div>
                </div>

                <PatientDetails
                    v-if="Object.keys(patientDetails).length"
                    :patient="patientDetails"
                    :shippingAddress="test.shippingAddress"
                    :gdEstimatedDeliveryDate="test.gdEstimatedDeliveryDate"
                    class="mt-4" 
                />

                <div class="mt-4">
                    <b-tabs content-class="mt-0">
                        <b-tab title="Test Results" active>
                            <TestResults 
                                :test="test"
                                :results="testResults"
                            />
                        </b-tab>
                        <b-tab :title="`Previous Tests (${previousPatientTests.totalCount})`">
                            <div class="card text-sm">
                                <div class="card-body">
                                    <PatientTests
                                        :tests="previousPatientTests"
                                        :patientId="patientDetails.id"
                                        :organisationId="organisationId"
                                        :showUpcomingTests="false"
                                    />
                                </div>
                            </div>
                        </b-tab>
                        <b-tab :title="`Upcoming Tests (${upcomingPatientTests.totalCount})`">
                            <div class="card text-sm">
                                <div class="card-body">
                                    <PatientTests
                                        :tests="upcomingPatientTests"
                                        :patientId="patientDetails.id"
                                        :organisationId="organisationId"
                                        :showUpcomingTests="true"
                                    />
                                </div>
                            </div>
                        </b-tab>
                    </b-tabs>
                </div>

                <TestEvents 
                    v-if="testEvents.length"
                    :events="testEvents"
                    :results="testResults"
                />
                    
                <TestNotes
                    :notes="testNotes"
                    :testId="$route.params.id"
                    v-on:reload="getTestNotes()"
                />

                <div class="fixed-bottom bg-white py-25">
                    <div class="container-fluid">
                        <div class="row">
                            <div class="col d-block d-md-none text-right center-mobile">
                                <button v-if="canShowRaiseAnIssue" :disabled="markCompleteLoading" @click="raiseIssue()" class="btn btn-yellow btn-lg lg-shadow" :class="{disabled : markCompleteLoading}">Raise issue</button>
                                <button v-if="canShowMarkComplete" :disabled="markCompleteLoading" @click="markComplete()" class="btn btn-success btn-lg lg-shadow ml-2 mt-md-0" :class="{disabled : markCompleteLoading}">Mark complete <span v-if="markCompleteLoading" class="btn-loading"></span></button>
                            </div>

                            <div class="col d-none d-md-block text-right center-mobile">
                                <button :disabled="markCompleteLoading || !canShowRaiseAnIssue" @click="raiseIssue()" class="btn btn-yellow btn-lg lg-shadow" :class="{disabled : markCompleteLoading || !canShowRaiseAnIssue}">Raise an issue</button>
                                <button :disabled="markCompleteLoading || !canShowMarkComplete" @click="markComplete()" class="btn btn-success btn-lg lg-shadow ml-2 mt-2 mt-md-0" :class="{disabled : markCompleteLoading || !canShowMarkComplete}">Mark as complete <span v-if="markCompleteLoading" class="btn-loading"></span></button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import {mapGetters, mapActions} from 'vuex'
import Breadcrumbs from '@/partials/site-structure/BreadcrumbsComponent.vue'
import Loader from '../../../Common/components/Loader.vue'
import CancelTestModal from '@/components/modals/CancelTestModalComponent.vue'
import RaiseIssueModal from '@/components/modals/RaiseIssueModalComponent.vue'
import PatientDetails from '../../../Common/components/PatientDetailsComponent.vue'
import TestResults from '@/components/TestResultsComponent.vue'
import TestEvents from '../../../Common/components/TestEventsComponent.vue'
import TestNotes from '@/components/TestNotesComponent.vue'
import PatientTests from '@/components/PatientTestsComponent.vue'
import TestStatus from '../../../Common/enums/TestStatus'
export default {
    name: 'ViewTest',

    components: {
        Loader,
        PatientDetails,
        TestResults,
        TestEvents,
        TestNotes,
        PatientTests,
        Breadcrumbs
    },

    data() {
        return {
            loading: true,
            isBusy: false,
            markCompleteLoading: false,
            test: {},
            patientDetails: {},
            testNotes: [],
            testEvents: [],
            testResults: {},
            previousPatientTests: {},
            upcomingPatientTests: {},
            CancelTestModal,
            RaiseIssueModal,
            TestStatus
        }
    },

    async mounted() {
        try {
            await this.getTest()
            await this.getPatientDetails()
            await this.getTestEvents()
            await this.getTestNotes()
            await this.getUpcomingPatientTests()
            await this.getPreviousPatientTests()

            if(this.test.resultId) {
                await this.getTestResults()
            }
        }
        catch {
            this.setNotification({
                type: 'error',
                title: 'Error',
                message: 'Something went wrong while fetching the data. Please try again'
            })
        }
        finally {
            this.loading = false
        }
    },

    watch: {
        shouldRefreshViewTestComponent: async function (val) {
            if(val === true) {
                this.loading = true
                this.isBusy = true

                this.test = {}
                this.patientDetails = {}
                this.testNotes = []
                this.testEvents = []
                this.testResults = {}
                this.previousPatientTests = {}
                this.upcomingPatientTests = {}

                try {
                    await this.getTest()
                    await this.getPatientDetails()
                    await this.getTestEvents()
                    await this.getTestNotes()
                    await this.getUpcomingPatientTests()
                    await this.getPreviousPatientTests()

                    if (this.test.resultId) {
                        await this.getTestResults()
                    }
                } catch {
                    this.loading = false
                    this.isBusy = false
                    this.setShouldRefreshViewTestComponent(false)
                    this.setNotification({
                        type: 'error',
                        title: 'Error',
                        message: 'Something went wrong while fetching the data. Please try again'
                    })
                } finally {
                    this.loading = false
                    this.isBusy = false
                    this.setShouldRefreshViewTestComponent(false)
                }
            }
        }
    },

    computed: {
        ...mapGetters({
            organisationId: 'UserStore/GetUserOrganisationId',
            shouldRefreshViewTestComponent: 'AppStore/GetShouldRefreshViewTestComponent'       
        }),

        canShowRaiseAnIssue() {
            return this.test.status == TestStatus.RETURNED || this.test.status == TestStatus.FAILED
        },

        canShowMarkComplete() {
            return this.test.status == TestStatus.RETURNED
        },

        canShowCancel() {
            return this.test.status != TestStatus.COMPLETE && this.test.status != TestStatus.CANCELLED && this.test.status != TestStatus.REPORTED
        }
    },

    methods: {
        ...mapActions({
            setNotification: 'NotificationStore/SetNotification',
            setShouldRefreshViewTestComponent: 'AppStore/SetShouldRefreshViewTestComponent'
        }),

        async getTest() {
            let response = await axios.get(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/${this.organisationId}/${this.$route.params.id}`)
        
            this.test = response.data
        },

        async getPatientDetails() {
            let response = await axios.get(`${process.env.VUE_APP_AXIOS_PATIENT_BASE_URL}/api/v1/patients/${this.organisationId}/${this.test.patientId}`)

            this.patientDetails = response.data
        },

        async getTestNotes() {
            let response = await axios.get(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/testnotes/${this.organisationId}/${this.$route.params.id}/100/0`)

            this.testNotes = response.data.results
        },

        async getTestEvents() {
            let response = await axios.get(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/testevents/${this.organisationId}/${this.$route.params.id}`)

            this.testEvents = response.data
        },
        
        async getTestResults() {
            let response = await axios.get(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/results/${this.organisationId}/${this.test.resultId}`)

            this.testResults = response.data
        },

        async getUpcomingPatientTests() {
            let searchModel = {
                organisationId: this.organisationId,
                facilityId: "",
                practitionerId: "",
                patientId: this.patientDetails.id,
                pageSize: 10,
                offset: 0,
                sortColumn: "TEST_NUMBER",
                sortType: "DESCENDING",
                testStatus: [TestStatus.NEW, TestStatus.ISSUED]
            }

            let response = await axios.post(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/search`, searchModel)
            this.upcomingPatientTests = response.data
        },

        async getPreviousPatientTests() {
            let searchModel = {
                organisationId: this.organisationId,
                facilityId: "",
                practitionerId: "",
                patientId: this.patientDetails.id,
                pageSize: 10,
                offset: 0,
                sortColumn: "TEST_NUMBER",
                sortType: "DESCENDING",
                testStatus: [TestStatus.RETURNED, TestStatus.COMPLETE, TestStatus.CANCELLED, TestStatus.REPORTED, TestStatus.FAILED, TestStatus.ERROR]
            }

            let response = await axios.post(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/search`, searchModel)
            this.previousPatientTests = response.data
        },

        async markComplete() {
            this.markCompleteLoading = true
            try {
                await axios.patch(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/${this.organisationId}/${this.$route.params.id}/complete`, {
                    reason: null
                })  
                
                this.setNotification({
                    type: 'success',
                    title: 'Success',
                    message: 'The test has been marked as completed'
                })

                return this.$router.push({name: 'Dashboard'})
            }
            catch {
                this.setNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Something went wrong completing the test. Please try again'
                })
            }
            finally {
                this.markCompleteLoading = false
            }
        },

        raiseIssue() {
            this.$modal.show(RaiseIssueModal, {organisationId: this.organisationId, test: this.test}, {width: '400px'})
        },

        cancelTest() {
            this.$modal.show(CancelTestModal, {testExists: true, organisationId: this.organisationId, testId: this.$route.params.id}, {width: '400px'})
        },

        async downloadResult (asPDF) {
            this.isBusy = true
            try {
                var downloadUrl = asPDF ? `${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/${this.organisationId}/${this.$route.params.id}/pdf` : `${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/${this.organisationId}/${this.$route.params.id}/csv`
                const response = await axios.get(downloadUrl, {
                        responseType: 'blob' // Set the responseType to 'blob'
                    })
                const url = asPDF ? window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })) : window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url
                if (asPDF) {
                    link.setAttribute('download', `Test_Data_${this.$route.params.id}.pdf`)
                } else {
                    link.setAttribute('download', `Test_Data_${this.$route.params.id}.csv`)
                }
                document.body.appendChild(link)
                link.click()
                link.remove()
            } catch {
                this.isBusy = false
                this.setNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Something went wrong. Please try again.'
                })
            } finally {
                this.isBusy = false
            }
        },

        async viewTest (testId) {
            await this.$router.push({ name: 'ViewTest', params: { id: testId } })
        }
    }
}
</script>